import React from "react";
import { cn } from "../../../lib/utils";
import { Bot, Server, ArrowRight } from "lucide-react";
import "../AgenticGateway.css";

interface FlowNode {
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  metadata?: Record<string, string>;
  badge?: React.ReactNode;
}

interface GatewayFlowDiagramProps {
  agentName?: string;
  serverName?: string;
  /** agent -> gateway session */
  sessionId?: string;
  /** gateway -> server (client) session */
  clientSessionId?: string;
  requestId?: string;
  correlationId?: string;
  originalToolName?: string;
  tokenMode?: string;
  className?: string;
}

// token-mode badge, mirrors the four backend-emitted values
// (PENDING | STS-OBO | AGENT-PROVIDED | CONFIG)
function tokenBadge(mode?: string): React.ReactNode {
  if (!mode) return null;
  const map: Record<string, { label: string; cls: string }> = {
    "STS-OBO": { label: "OBO Token", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
    "AGENT-PROVIDED": { label: "Agent Token", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
    PENDING: { label: "Pending", cls: "bg-muted-foreground/15 text-muted-foreground border-muted-foreground/30" },
    CONFIG: { label: "Config", cls: "bg-muted-foreground/15 text-muted-foreground border-muted-foreground/30" },
  };
  const b = map[mode] || { label: mode, cls: "bg-muted-foreground/15 text-muted-foreground border-muted-foreground/30" };
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border", b.cls)}>
      {b.label}
    </span>
  );
}

export default function GatewayFlowDiagram({
  agentName, serverName, sessionId, clientSessionId, requestId, correlationId,
  originalToolName, tokenMode, className,
}: GatewayFlowDiagramProps) {
  const nodes: FlowNode[] = [
    {
      label: agentName || "AI Agent",
      sublabel: "Agent",
      icon: <Bot className="w-5 h-5" />,
      metadata: {
        ...(sessionId ? { "Session": sessionId } : {}),
        ...(requestId ? { "Request": requestId } : {}),
      },
    },
    {
      label: "WS Gateway",
      sublabel: "Orchestration",
      icon: <Server className="w-5 h-5" />,
      metadata: {
        ...(correlationId ? { "Correlation": correlationId } : {}),
      },
      badge: tokenMode ? tokenBadge(tokenMode) : undefined,
    },
    {
      label: serverName || "MCP Server",
      sublabel: "Target",
      icon: <Server className="w-5 h-5" />,
      metadata: {
        ...(originalToolName ? { "Tool": originalToolName } : {}),
        ...(clientSessionId ? { "Session": clientSessionId } : {}),
      },
    },
  ];

  return (
    <div className={cn("flex items-center justify-center gap-2 py-4", className)}>
      {nodes.map((node, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-2 min-w-[140px]">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl ag-surface border border-border text-primary">
              {node.icon}
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-foreground truncate max-w-[160px]">{node.label}</div>
              {node.sublabel && <div className="text-xs text-muted-foreground">{node.sublabel}</div>}
            </div>
            {node.badge && <div>{node.badge}</div>}
            {node.metadata && Object.keys(node.metadata).length > 0 && (
              <div className="space-y-0.5 text-center">
                {Object.entries(node.metadata).map(([k, v]) => (
                  <div key={k} className="text-[10px] text-muted-foreground font-mono truncate max-w-[160px]">{k}: {v}</div>
                ))}
              </div>
            )}
          </div>
          {i < nodes.length - 1 && (
            <ArrowRight className="w-5 h-5 text-muted-foreground/70 shrink-0 mx-1" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
